import { Api } from "@/services/ApiService"
import { SchultenMedia } from "@/services/DataService"

class TermsService {
    private latestTerms: any = null;
    private _needsAcceptance: boolean | null = null;

    async getTerms() {
        try {
            const responseResult = await Api.get(SchultenMedia.API.terms.get)
            const response = responseResult.ok ? responseResult.data : null
            
            if (!response || response?.error) {
                return {
                    success: false,
                    message: response?.message || "Failed to fetch terms.",
                    content: null
                }
            }

            const content = response.data?.terms || response.terms;
            const version = response.data?.updated_at || response.updated_at;

            if (!content) {
                return {
                    success: true,
                    content: null,
                    version: null
                }
            }

            this.latestTerms = {
                content: content,
                version: version
            };

            return {
                success: true,
                ...this.latestTerms
            }
        } catch (error) {
            return {
                success: false,
                message: "An unexpected error occurred while fetching terms.",
                content: null
            }
        }
    }

    async needsAcceptance(forceRefresh = false): Promise<boolean> {
        if (!SchultenMedia.Authenticated || !SchultenMedia.User) {
            this._needsAcceptance = false;
            return false;
        }

        if (this._needsAcceptance !== null && !forceRefresh) {
            return this._needsAcceptance;
        }

        const user = SchultenMedia.User;
        const userAcceptedAt = user.terms_accepted_at;
        const hasAcceptedTerms = user.has_accepted_terms;
        
        // If the backend explicitly says terms are accepted (latest version), trust it
        if (hasAcceptedTerms === true || hasAcceptedTerms === 1 || hasAcceptedTerms === "1") {
            this._needsAcceptance = false;
            return false;
        }

        // Fetch terms to get the latest version/date for comparison
        const response = await this.getTerms();
        
        // If terms content is empty, skip the acceptance flow entirely
        if (response.success && !response.content) {
            this._needsAcceptance = false;
            return false;
        }
        
        if (response.success && response.version) {
            // If we have no record of acceptance at all, they need to accept
            if (!userAcceptedAt && hasAcceptedTerms !== true && hasAcceptedTerms !== 1) {
                this._needsAcceptance = true;
                return true;
            }

            // Fix date parsing for Safari (replace space with T for ISO compatibility)
            const parseDate = (d: any) => {
                if (!d) return new Date(0);
                const normalized = typeof d === 'string' ? d.replace(' ', 'T') : d;
                return new Date(normalized);
            };

            const termsDate = parseDate(response.version);
            const userDate = parseDate(userAcceptedAt);
            
            // Check if dates are valid before comparing
            if (isNaN(termsDate.getTime()) || isNaN(userDate.getTime())) {
                // If dates are invalid, fallback to the boolean check we already did
                this._needsAcceptance = !userAcceptedAt && hasAcceptedTerms !== true;
            } else {
                this._needsAcceptance = termsDate > userDate;
            }
            
            return this._needsAcceptance;
        }

        // Final fallback if terms could not be fetched or have no version
        this._needsAcceptance = !userAcceptedAt && hasAcceptedTerms !== true && hasAcceptedTerms !== 1;
        return this._needsAcceptance;
    }

    async acceptTerms() {
        try {
            const response = await Api.post(SchultenMedia.API.terms.accept, {})

            if (response?.error) {
                return {
                    success: false,
                    message: response.message || "Failed to accept terms.",
                }
            }

            // Update local user state
            if (SchultenMedia.User) {
                SchultenMedia.User.terms_accepted_at = new Date().toISOString()
            }
            
            this._needsAcceptance = false;

            return {
                success: true,
                message: response.message || "Terms accepted successfully",
            }
        } catch (error) {
            return {
                success: false,
                message: "An unexpected error occurred while accepting terms",
            }
        }
    }

    clearCache() {
        this._needsAcceptance = null;
        this.latestTerms = null;
    }
}

export default new TermsService()
